import React, { useState } from 'react';
import { ApiRequest, ToastBuilder, ButtonBuilder } from '../../../../../../../../GlobalComponents/Builders';
import useConfirmModal from '../../../../../../../../GlobalComponents/Builders/ConfirmModal';
import { showTimestamp } from '../../../../../../../../GlobalComponents/Helpers';

/**
 * RecordStateCacheViewer — Displays the state cache for a specific record.
 * 
 * Props:
 *   jobId        - The job ID
 *   resourceId   - The resource ID (e.g., tab.resource_id)
 *   sourceRecId  - The source record ID
 *   isSync       - Whether this is a sync job
 *   autoLoad     - If true, fetches cache on mount (default: false)
 *   compact      - If true, hides the header/buttons (for inline use)
 *   allowClear   - If true, shows a red Clear button that clears the whole cache after confirmation
 */

const RecordStateCacheViewer = ({ jobId, resourceId, sourceRecId, isSync = false, autoLoad = false, compact = false, allowClear = false }) => {

    const [cacheData, setCacheData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const { confirm, ConfirmModal } = useConfirmModal();

    const fetchCache = async () => {
        setCacheData(null);
        setLoading(true);
        const res = await ApiRequest('POST', '/dmt/jobs/{jobId}/actions/{actionName}', {
            pathParams: { jobId, actionName: 'view-state-cache' },
            body: {
                sourceResourceId: resourceId,
                formData: { sourceRecId: parseInt(sourceRecId) },
            },
        });
        setLoading(false);
        setLoaded(true);
        if (res.success) {
            setCacheData(res.data?.cache || null);
        } else {
            ToastBuilder('error', res.error || 'Failed to fetch state cache');
        }
    };

    const clearCache = async () => {
        const confirmed = await confirm('Clear the entire state cache for this record? This cannot be undone.', {
            title: 'Clear All State Cache',
            confirmLabel: 'Clear All',
            cancelLabel: 'Cancel',
        });
        if (!confirmed) return;

        const res = await ApiRequest('POST', '/dmt/jobs/{jobId}/actions/{actionName}', {
            pathParams: { jobId, actionName: 'clear-state-cache' },
            body: {
                sourceResourceId: resourceId,
                formData: { sourceRecId: parseInt(sourceRecId), isSync: !!isSync, mode: 'all' },
            },
        });
        if (res.success) {
            ToastBuilder('success', res.data?.message || 'Cache cleared');
            fetchCache();
        } else {
            ToastBuilder('error', res.error || 'Failed to clear cache');
        }
    };

    const clearKey = async (key, displayName) => {
        const confirmed = await confirm(`Clear the cached key "${displayName || key}" for this record? This cannot be undone.`, {
            title: 'Clear Cache Key',
            confirmLabel: 'Clear',
            cancelLabel: 'Cancel',
        });
        if (!confirmed) return;

        const res = await ApiRequest('POST', '/dmt/jobs/{jobId}/actions/{actionName}', {
            pathParams: { jobId, actionName: 'clear-state-cache' },
            body: {
                sourceResourceId: resourceId,
                formData: { sourceRecId: parseInt(sourceRecId), isSync: !!isSync, mode: 'keys', keys: [key] },
            },
        });
        if (res.success) {
            ToastBuilder('success', res.data?.message || `Cleared "${displayName || key}"`);
            fetchCache();
        } else {
            ToastBuilder('error', res.error || 'Failed to clear key');
        }
    };

    // Auto-load on mount if requested
    React.useEffect(() => {
        if (autoLoad && jobId && resourceId && sourceRecId) {
            fetchCache();
        }
    }, [autoLoad, jobId, resourceId, sourceRecId]);

    const renderDiff = () => {
        if (loading) return <p style={{ textAlign: 'center', padding: 20 }}>Loading...</p>;
        if (!cacheData) return <p style={{ textAlign: 'center', padding: 20, color: '#999' }}>No state cache found for this record.</p>;

        const { tracked_fields, current_state, previous_state, oldest_state, last_updated } = cacheData;

        const fields = tracked_fields || [];
        const fieldKeys = fields.map(f => typeof f === 'object' ? Object.keys(f)[0] : f);

        // Separate persistent data keys from regular keys
        const persistentPrefix = '_persistent_data_/';
        const allStateKeys = current_state ? Object.keys(current_state) : [];
        const persistentKeys = allStateKeys.filter(k => k.startsWith(persistentPrefix));
        const regularKeys = fieldKeys.filter(k => k !== '_persistent_data_');

        const getVal = (state, key) => state ? state[key] : undefined;

        const formatVal = (val) => {
            if (val === undefined || val === null) return <span style={{ opacity: 0.4, fontStyle: 'italic' }}>null</span>;
            if (typeof val === 'object') return <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{JSON.stringify(val)}</span>;
            if (typeof val === 'boolean') return <span style={{ fontWeight: 600, color: val ? '#198754' : '#dc3545' }}>{String(val)}</span>;
            return <span>{String(val)}</span>;
        };

        const renderFieldRow = (key, displayName) => {
            const curr = getVal(current_state, key);
            const prev = getVal(previous_state, key);
            const old = getVal(oldest_state, key);
            const currStr = JSON.stringify(curr);
            const prevStr = JSON.stringify(prev);
            const oldStr = JSON.stringify(old);
            const prevDiff = currStr !== prevStr;
            const oldDiff = prevStr !== oldStr;
            const hasChange = prevDiff || oldDiff;

            return (
                <div key={key} style={{ padding: '6px 8px', borderBottom: '1px solid rgba(128,128,128,0.1)', background: hasChange ? 'rgba(253,126,20,0.06)' : 'transparent' }}>
                    <div className='d-flex align-items-baseline' style={{ gap: 8 }}>
                        <span style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: 11, minWidth: 0, flexShrink: 0 }}>{displayName || key}</span>
                        <span style={{ color: 'rgba(128,128,128,0.4)' }}>:</span>
                        <span style={hasChange ? { color: '#28a745', fontWeight: 600 } : {}}>{formatVal(curr)}</span>
                        {allowClear && (
                            <ButtonBuilder form='' label='Clear' className='btn-danger btn-sm ms-auto py-0 px-2' style={{ fontSize: 10, flexShrink: 0 }}
                                onClick={() => clearKey(key, displayName)} useSpinner={false} />
                        )}
                    </div>
                    {hasChange && (
                        <div style={{ paddingLeft: 16, marginTop: 3, fontSize: 11 }}>
                            {prevDiff && <div><span style={{ color: '#fd7e14', fontWeight: 600 }}>prev:</span> {formatVal(prev)}</div>}
                            {oldDiff && <div><span style={{ color: '#6c757d', fontWeight: 600 }}>oldest:</span> {formatVal(old)}</div>}
                        </div>
                    )}
                </div>
            );
        };

        const allDisplayKeys = [...regularKeys, ...persistentKeys];
        const changedCount = allDisplayKeys.filter(key => {
            const c = JSON.stringify(getVal(current_state, key));
            const p = JSON.stringify(getVal(previous_state, key));
            const o = JSON.stringify(getVal(oldest_state, key));
            return c !== p || p !== o;
        }).length;

        return (
            <div style={{ fontSize: 12 }}>
                <div className='d-flex justify-content-between mb-2'>
                    <span><strong>{allDisplayKeys.length}</strong> tracked field(s){changedCount === 0 && <span className='text-muted' style={{ marginLeft: 8 }}>— all consistent</span>}{changedCount > 0 && <span style={{ marginLeft: 8, color: '#fd7e14' }}>— {changedCount} changed</span>}</span>
                    <span className='text-muted' style={{ fontSize: 11 }}>Updated: {last_updated ? showTimestamp(last_updated * 1000) : '—'}</span>
                </div>
                <div>
                    {regularKeys.map(key => renderFieldRow(key, key))}

                    {persistentKeys.length > 0 && (
                        <div style={{ marginTop: 8 }}>
                            <div style={{ padding: '6px 8px', background: 'rgba(111,66,193,0.06)', borderBottom: '1px solid rgba(111,66,193,0.15)', borderTop: '1px solid rgba(111,66,193,0.15)' }}>
                                <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: '#6f42c1', letterSpacing: 0.5 }}>Persistent Data</span>
                            </div>
                            {persistentKeys.map(key => {
                                const displayName = key.replace(persistentPrefix, '');
                                return renderFieldRow(key, displayName);
                            })}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    if (compact) {
        return (
            <div>
                {!loaded && !loading && (
                    <ButtonBuilder form='' label='Load Cache' className='btn-outline-secondary btn-sm' style={{ fontSize: 11 }}
                        onClick={fetchCache} useSpinner={false} />
                )}
                {(loading || loaded) && renderDiff()}
                {loaded && (
                    <div className='mt-2 d-flex' style={{ gap: 8 }}>
                        <ButtonBuilder form='' label='Refresh' className='btn-outline-secondary btn-sm' style={{ fontSize: 10 }}
                            onClick={fetchCache} useSpinner={false} />
                        {allowClear && cacheData && (
                            <ButtonBuilder form='' label='Clear All' className='btn-danger btn-sm' style={{ fontSize: 10 }}
                                onClick={clearCache} useSpinner={false} />
                        )}
                    </div>
                )}
                <ConfirmModal />
            </div>
        );
    }

    return (
        <div>
            <div className='d-flex align-items-center justify-content-between'>
                <span style={{ fontSize: 12, fontWeight: 600, color: '#6c757d' }}>State Cache</span>
                <div className='d-flex' style={{ gap: 8 }}>
                    <ButtonBuilder form='' label={cacheData ? 'Refresh' : 'Load Cache'} className='btn-outline-secondary btn-sm' style={{ fontSize: 11 }}
                        onClick={fetchCache} useSpinner={false} />
                    {allowClear && cacheData && (
                        <ButtonBuilder form='' label='Clear All' className='btn-danger btn-sm' style={{ fontSize: 11 }}
                            onClick={clearCache} useSpinner={false} />
                    )}
                </div>
            </div>
            {(loading || loaded) && <div style={{ marginTop: 8 }}>{renderDiff()}</div>}
            <ConfirmModal />
        </div>
    );
};

export default RecordStateCacheViewer;
